import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { triggerBrowserDownload, uploadToS3 } from '@/utils/upload'
import { type FileFilters, filesApi } from '../api/files.api'
import { filesKeys } from './query-keys'

export type {
	DownloadUrlResponse,
	FileFilters,
	FileRecord,
	FileShareRecord,
	FileUpdateInput,
	UploadUrlInput,
	UploadUrlResponse,
} from '../api/files.api'

export const useFiles = (filters: FileFilters = {}) =>
	useQuery({
		queryKey: filesKeys.list(filters),
		queryFn: () => filesApi.list(filters),
	})

export const useFile = (fileId: string) =>
	useQuery({
		queryKey: filesKeys.detail(fileId),
		queryFn: () => filesApi.get(fileId),
		enabled: !!fileId,
	})

export const useUploadFile = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (params: {
			file: File
			entityType?: string
			entityId?: string
			isPublic?: boolean
			description?: string
		}) => {
			// Phase 1: Request upload URL
			const { file: fileRecord, uploadUrl } = await filesApi.requestUploadUrl({
				fileName: params.file.name,
				contentType: params.file.type,
				entityType: params.entityType,
				entityId: params.entityId,
				isPublic: params.isPublic,
				description: params.description,
			})

			// Phase 2: Upload directly to S3
			await uploadToS3(uploadUrl, params.file)

			// Phase 3: Confirm upload
			const confirmed = await filesApi.confirmUpload(fileRecord.fileId)
			return confirmed
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: filesKeys.all }),
	})
}

export const useDownloadFile = () =>
	useMutation({
		mutationFn: async (fileId: string) => {
			const { downloadUrl, fileName } = await filesApi.getDownloadUrl(fileId)
			triggerBrowserDownload(downloadUrl, fileName)
			return { downloadUrl, fileName }
		},
	})

export const useUpdateFile = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: string
			data: { description?: string; entityType?: string; entityId?: string }
		}) => filesApi.update(id, data),
		onSuccess: () => void qc.invalidateQueries({ queryKey: filesKeys.all }),
	})
}

export const useDeleteFile = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (fileId: string) => filesApi.delete(fileId),
		onSuccess: () => void qc.invalidateQueries({ queryKey: filesKeys.all }),
	})
}
